/**
 * Event schedule helpers.
 *
 * An event's schedule is a list of sessions — the periods it is actually on.
 * Three shapes, one model (see migration 091):
 *
 *   single      one session              Sat 09:00–11:00
 *   continuous  one multi-day session    Fri 18:00 → Sun 23:00, never closes
 *   sessions    one row per open day     Sat 09:00–19:00, Sun 10:00–18:00
 *   recurring   one row per occurrence   every Sat & Sun 10:00–16:00 until 28 Feb
 *
 * `events.start_time` / `events.end_time` mirror the overall span and are
 * maintained by a DB trigger, so callers that only need the span can keep using
 * those. Anything that needs to know whether the event is *open right now*, or
 * how to describe it, goes through here — the same way `src/lib/capacity.ts`
 * owns the meaning of a null capacity.
 */

import { toLocalDateKey, parseLocalDateKey } from './utils';

export interface EventSession {
  id?: string;
  starts_at: string;
  ends_at: string;
}

export type ScheduleKind = 'single' | 'continuous' | 'sessions' | 'recurring';

/** Where the event is in its life right now. */
export type ScheduleState =
  /** Hasn't started yet. */
  | 'upcoming'
  /** A session is running right now. */
  | 'open'
  /** Started, not finished, but between sessions (e.g. overnight on a hybrid schedule). */
  | 'closed'
  /** Every session is in the past. */
  | 'ended';

export interface ScheduleStatus {
  state: ScheduleState;
  /** The session running now, when state is 'open'. */
  current: EventSession | null;
  /** The next session due to start, when state is 'upcoming' or 'closed'. */
  next: EventSession | null;
}

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function startMs(s: EventSession): number {
  return new Date(s.starts_at).getTime();
}

function endMs(s: EventSession): number {
  return new Date(s.ends_at).getTime();
}

/** Sessions in chronological order. Never mutates the input. */
export function sortSessions(sessions: EventSession[]): EventSession[] {
  return [...sessions].sort((a, b) => startMs(a) - startMs(b));
}

/**
 * Sessions for an event, falling back to the legacy single-period shape when an
 * event has no session rows (an old row, or one read through a query that
 * didn't join them). Keeps every caller from having to handle the empty case.
 */
export function sessionsOrFallback(
  sessions: EventSession[] | null | undefined,
  startTime: string,
  endTime?: string | null,
): EventSession[] {
  if (sessions && sessions.length > 0) return sortSessions(sessions);
  const ends = endTime ?? new Date(new Date(startTime).getTime() + TWO_HOURS_MS).toISOString();
  return [{ starts_at: startTime, ends_at: ends }];
}

/**
 * Number of distinct local calendar days the event touches. A session running
 * 22:00–02:00 counts as two days, which is what a reader expects to see.
 */
export function dayCount(sessions: EventSession[]): number {
  const days = new Set<string>();
  for (const s of sessions) {
    // Walk local midnights so a DST shift can't drift the count.
    const cursor = new Date(startMs(s));
    cursor.setHours(0, 0, 0, 0);
    // Inclusive last instant: an end of exactly midnight belongs to the day
    // before, so a 09:00–00:00 session is one day, not two.
    const lastMs = endMs(s) - 1;
    while (cursor.getTime() <= lastMs) {
      days.add(toLocalDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(0, 0, 0, 0);
    }
  }
  return days.size;
}

export function isMultiDay(sessions: EventSession[]): boolean {
  return dayCount(sessions) > 1;
}

/** Does this session overlap the given local day (a YYYY-MM-DD key)? */
export function sessionOverlapsDay(session: EventSession, dateKey: string): boolean {
  const dayStart = parseLocalDateKey(dateKey).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  // Half-open interval on both sides: a session ending exactly at midnight does
  // not count as being on the following day.
  return startMs(session) < dayEnd && endMs(session) > dayStart;
}

/** Does any session overlap the given local day? */
export function isOnDay(sessions: EventSession[], dateKey: string): boolean {
  return sessions.some((s) => sessionOverlapsDay(s, dateKey));
}

/** Is a session running at `now`? */
export function isSessionOpen(session: EventSession, now: number = Date.now()): boolean {
  return startMs(session) <= now && endMs(session) > now;
}

export function scheduleState(
  sessions: EventSession[],
  now: number = Date.now(),
): ScheduleStatus {
  const sorted = sortSessions(sessions);
  if (sorted.length === 0) return { state: 'ended', current: null, next: null };

  const current = sorted.find((s) => isSessionOpen(s, now)) ?? null;
  if (current) return { state: 'open', current, next: null };

  const next = sorted.find((s) => startMs(s) > now) ?? null;
  if (!next) return { state: 'ended', current: null, next: null };

  // Before the first session the event hasn't begun; between sessions it has.
  const hasBegun = startMs(sorted[0]) <= now;
  return { state: hasBegun ? 'closed' : 'upcoming', current: null, next };
}

/**
 * When an event next matters to a reader, as a timestamp: now if a session is
 * running, otherwise the start of the next one. Ended events sort last.
 */
export function nextOccurrenceMs(sessions: EventSession[], now: number = Date.now()): number {
  const status = scheduleState(sessions, now);
  if (status.state === 'open') return now;
  if (status.next) return startMs(status.next);
  return Number.POSITIVE_INFINITY;
}

interface Schedulable {
  start_time: string;
  end_time?: string | null;
  event_sessions?: EventSession[] | null;
  score?: number;
}

/**
 * Feed order within a section: happening now first, then soonest to latest
 * (a repeating event counts from its next session, not its first), with the
 * recommendation score breaking ties.
 */
export function compareByWhen(a: Schedulable, b: Schedulable, now: number = Date.now()): number {
  const at = nextOccurrenceMs(sessionsOrFallback(a.event_sessions, a.start_time, a.end_time), now);
  const bt = nextOccurrenceMs(sessionsOrFallback(b.event_sessions, b.start_time, b.end_time), now);
  if (at !== bt) return at < bt ? -1 : 1;
  return (b.score ?? 0) - (a.score ?? 0);
}

/** True when the sessions cover their whole span with no gap — a 24/7 event. */
export function isContinuous(sessions: EventSession[]): boolean {
  const sorted = sortSessions(sessions);
  if (sorted.length === 0) return false;
  if (sorted.length === 1) return isMultiDay(sorted);
  for (let i = 1; i < sorted.length; i++) {
    if (startMs(sorted[i]) > endMs(sorted[i - 1])) return false;
  }
  return isMultiDay(sorted);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** A weekly pattern read back off the stored sessions. */
export interface WeeklyPattern {
  /** Weekdays the event is on, Monday first (0 = Sunday). */
  weekdays: number[];
  /** Local HH:MM–HH:MM hours for each weekday, keyed by weekday number. */
  hours: Record<number, { open: string; close: string }>;
  first: EventSession;
  last: EventSession;
  count: number;
}

const MON_FIRST = [1, 2, 3, 4, 5, 6, 0];

function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Is this a weekly repeat — the same weekdays, at the same hours, every week
 * from the first occurrence to the last? Detected from the sessions rather
 * than trusted from `schedule_kind`, so every card and list row that only has
 * the sessions to hand describes it correctly, and a "Multiple days" event
 * whose host happened to turn on only the Saturdays reads the same way.
 *
 * Returns null for anything else: a single day, a continuous run, a span with
 * an irregular set of days, or fewer than two weeks of occurrences.
 */
export function weeklyPattern(sessions: EventSession[]): WeeklyPattern | null {
  const sorted = sortSessions(sessions);
  if (sorted.length < 2) return null;

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  // Needs at least a full week between first and last start, or it's just a
  // short span of days that happens to be listed in order.
  if (startMs(last) - startMs(first) < 7 * DAY_MS - 1) return null;

  const hours: Record<number, { open: string; close: string }> = {};
  const seenDays = new Set<string>();
  for (const s of sorted) {
    // Every occurrence must be a single day's worth (past-midnight is fine).
    if (endMs(s) - startMs(s) > DAY_MS) return null;
    const key = toLocalDateKey(new Date(startMs(s)));
    if (seenDays.has(key)) return null;
    seenDays.add(key);

    const day = new Date(startMs(s)).getDay();
    const open = hhmm(startMs(s));
    const close = hhmm(endMs(s));
    const existing = hours[day];
    if (existing && (existing.open !== open || existing.close !== close)) return null;
    hours[day] = { open, close };
  }

  // The pattern must be unbroken: every one of its weekdays between the first
  // and last occurrence has a session.
  const weekdaySet = new Set(Object.keys(hours).map(Number));
  let expected = 0;
  const cursor = new Date(startMs(first));
  cursor.setHours(0, 0, 0, 0);
  const lastDay = new Date(startMs(last));
  lastDay.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= lastDay.getTime()) {
    if (weekdaySet.has(cursor.getDay())) expected++;
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(0, 0, 0, 0);
  }
  if (expected !== sorted.length) return null;

  return {
    weekdays: MON_FIRST.filter((d) => weekdaySet.has(d)),
    hours,
    first,
    last,
    count: sorted.length,
  };
}

/** Short weekday name for a weekday number, in the user's locale. */
export function weekdayName(day: number, format: 'short' | 'long' = 'short'): string {
  // 4 Jan 2026 is a Sunday, so day N of that week is weekday N.
  return new Date(2026, 0, 4 + day).toLocaleDateString(undefined, { weekday: format });
}

/**
 * "Every Sat & Sun", "Every Mon, Wed & Fri", "Every day", "Every weekend".
 */
export function formatWeekdays(weekdays: number[]): string {
  const set = new Set(weekdays);
  if (set.size === 7) return 'Every day';
  if (set.size === 2 && set.has(6) && set.has(0)) return 'Every weekend';
  if (set.size === 5 && [1, 2, 3, 4, 5].every((d) => set.has(d))) return 'Every weekday';
  const names = MON_FIRST.filter((d) => set.has(d)).map((d) => weekdayName(d));
  if (names.length === 1) return `Every ${names[0]}`;
  return `Every ${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function weekdayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short' });
}

function dayAndMonth(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Compact description of when the event runs, for cards and list rows.
 *   single day      "Sat 9 Oct"
 *   multi-day       "Sat–Sun · 2 days"
 *   continuous      "Fri–Sun · runs 24/7"
 *   recurring       "Every weekend until 28 Feb"
 *   rolling weekly  "Every weekend · ongoing"
 *
 * @param repeatWeekly `events.repeat_weekly` (116). A rolling event's last
 *   stored session is just how far the top-up has reached, not an end date, so
 *   saying "until 25 Dec" would promise an ending that isn't real.
 */
export function formatSpan(sessions: EventSession[], repeatWeekly = false): string {
  const sorted = sortSessions(sessions);
  if (sorted.length === 0) return '';

  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  if (!isMultiDay(sorted)) {
    return `${weekdayLabel(first.starts_at)} ${dayAndMonth(first.starts_at)}`;
  }

  const weekly = weeklyPattern(sorted);
  if (weekly) {
    return repeatWeekly
      ? `${formatWeekdays(weekly.weekdays)} · ongoing`
      : `${formatWeekdays(weekly.weekdays)} until ${dayAndMonth(weekly.last.starts_at)}`;
  }

  const days = dayCount(sorted);
  const range = `${weekdayLabel(first.starts_at)}–${weekdayLabel(last.ends_at)}`;
  return isContinuous(sorted) ? `${range} · runs 24/7` : `${range} · ${days} days`;
}

/**
 * Live state line for cards and the event page.
 *   open              "Open now · closes 19:00"
 *   closed, reopening "Closed · opens Sun 10:00"
 *   upcoming today    "Starts 09:00"
 *   upcoming later    "Sat 09:00–19:00"
 *   ended             "Ended"
 */
export function formatLiveState(sessions: EventSession[], now: number = Date.now()): string {
  const status = scheduleState(sessions, now);

  switch (status.state) {
    case 'open':
      return `Open now · closes ${timeLabel(status.current!.ends_at)}`;
    case 'closed': {
      const next = status.next!;
      return `Closed · opens ${weekdayLabel(next.starts_at)} ${timeLabel(next.starts_at)}`;
    }
    case 'upcoming': {
      const next = status.next!;
      const todayKey = toLocalDateKey(new Date(now));
      if (toLocalDateKey(new Date(next.starts_at)) === todayKey) {
        return `Starts ${timeLabel(next.starts_at)}`;
      }
      return `${weekdayLabel(next.starts_at)} ${timeLabel(next.starts_at)}–${timeLabel(next.ends_at)}`;
    }
    case 'ended':
    default:
      return 'Ended';
  }
}

/**
 * Short suffix for list rows that already show a start time, e.g. "2 days",
 * "24/7" or "Every weekend". Null for a single-day event, so callers can append conditionally
 * without restating the single-day case everywhere:
 *
 *   {formatEventTime(event.start_time)}{suffix && ` · ${suffix}`}
 */
export function formatSpanSuffix(sessions: EventSession[]): string | null {
  const sorted = sortSessions(sessions);
  if (sorted.length === 0 || !isMultiDay(sorted)) return null;
  const weekly = weeklyPattern(sorted);
  if (weekly) return formatWeekdays(weekly.weekdays);
  return isContinuous(sorted) ? '24/7' : `${dayCount(sorted)} days`;
}

/**
 * Per-day rows for the event page, e.g.
 *   [{ label: 'Sat 9 Oct', hours: '09:00 – 19:00' }, …]
 * A continuous event collapses to a single row describing the whole run, and a
 * weekly repeat to one row per weekday plus the date range, rather than listing
 * every one of forty Saturdays.
 */
export function formatSessionRows(
  sessions: EventSession[],
  repeatWeekly = false,
): { key: string; label: string; hours: string }[] {
  const sorted = sortSessions(sessions);

  if (isContinuous(sorted) && sorted.length >= 1) {
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return [
      {
        key: 'continuous',
        label: 'Runs continuously',
        hours: `${weekdayLabel(first.starts_at)} ${timeLabel(first.starts_at)} → ${weekdayLabel(last.ends_at)} ${timeLabel(last.ends_at)}`,
      },
    ];
  }

  const weekly = weeklyPattern(sorted);
  if (weekly) {
    const rows = weekly.weekdays.map((day) => ({
      key: `weekday-${day}`,
      label: `Every ${weekdayName(day, 'long')}`,
      hours: `${weekly.hours[day].open} – ${weekly.hours[day].close}`,
    }));
    // A rolling event's stored dates only reach as far as the last top-up, so
    // showing them as a range would read as an end date the host never set.
    rows.push(
      repeatWeekly
        ? { key: 'range', label: 'Ongoing', hours: 'No end date' }
        : {
            key: 'range',
            label: `${dayAndMonth(weekly.first.starts_at)} – ${dayAndMonth(weekly.last.starts_at)}`,
            hours: `${weekly.count} dates`,
          }
    );
    return rows;
  }

  return sorted.map((s, i) => ({
    key: s.id ?? `${s.starts_at}-${i}`,
    label: `${weekdayLabel(s.starts_at)} ${dayAndMonth(s.starts_at)}`,
    hours: `${timeLabel(s.starts_at)} – ${timeLabel(s.ends_at)}`,
  }));
}
